import { useAuthStore } from '@/stores/auth.store';

/** Permission keys the ticketbox POS gates on. */
export const PERM = {
  /** Entry to the POS at all. */
  OPERATE: 'sale.ticketbox.operate',
  /** Ask a supervisor to void a paid sale. */
  VOID_REQUEST: 'sale.void.request',
  /** Ask a supervisor to refund a paid sale (full or partial). */
  REFUND_REQUEST: 'sale.refund.request',
  /** Issue free-of-charge tickets (the FOC page). Without OPERATE, the only page. */
  FOC_ISSUE: 'sale.foc.issue',
} as const;

/** Shown when an account lacks POS access (login refusal, RoleGate bounce). */
export const NO_POS_ACCESS_MESSAGE = 'Akun ini tidak memiliki akses kasir ticketbox.';

export interface PermissionSet {
  permissions: string[] | null | undefined;
  wildcards?: string[] | null;
}

/**
 * True when `key` is granted: an exact key, the global `*`, or a `prefix.*`
 * pattern covering it. A null set (not loaded yet) grants nothing.
 */
export function hasPermission(set: PermissionSet | null | undefined, key: string): boolean {
  if (!set || !set.permissions) return false;
  const all = [...(set.wildcards ?? []), ...set.permissions];
  for (const p of all) {
    if (p === '*' || p === key) return true;
    if (p.endsWith('.*') && key.startsWith(p.slice(0, -1))) return true;
  }
  return false;
}

/** May this account open the ticketbox POS? A cashier, or an FOC issuer (FOC page only). */
export function canOperatePos(set: PermissionSet | null | undefined): boolean {
  return hasPermission(set, PERM.OPERATE) || hasPermission(set, PERM.FOC_ISSUE);
}

/** An FOC issuer who is not a cashier: the ticketbox shows them the FOC flow alone. */
export function isFocOnly(set: PermissionSet | null | undefined): boolean {
  return hasPermission(set, PERM.FOC_ISSUE) && !hasPermission(set, PERM.OPERATE);
}

/**
 * The pages an FOC-only account may open: the FOC catalog, its document,
 * Details, Checkout, and the workstation setup every operator needs first.
 */
export const FOC_ONLY_PATHS: readonly string[] = ['/foc', '/foc/document', '/details', '/checkout', '/setup'];

/**
 * Where the ticketbox sends this account instead of `pathname`, or null to stay.
 * An FOC-only account is kept inside the FOC flow; an account without the FOC
 * key is kept out of it. One rule for the route gate, the rail and the palette.
 */
export function posRedirectFor(set: PermissionSet | null | undefined, pathname: string): string | null {
  if (isFocOnly(set)) return FOC_ONLY_PATHS.includes(pathname) ? null : '/foc';
  const inFoc = pathname === '/foc' || pathname.startsWith('/foc/');
  if (inFoc && !hasPermission(set, PERM.FOC_ISSUE)) return '/';
  return null;
}

/** React hook: the signed-in operator's permission set. */
export function usePermissionSet(): PermissionSet {
  const permissions = useAuthStore((s) => s.permissions);
  const wildcards = useAuthStore((s) => s.wildcards);
  return { permissions, wildcards };
}

/** React hook: does the signed-in operator hold `key`? */
export function useCan(key: string): boolean {
  return useAuthStore((s) => hasPermission({ permissions: s.permissions, wildcards: s.wildcards }, key));
}
